import React, { useState, useCallback, useRef } from 'react';
import { Upload, X, CheckCircle, AlertCircle, FileImage, Loader2, FolderOpen, Tag } from 'lucide-react';
import config from '@/config';
const BatchImageUploader = ({ 
  imageKind = 'original',
  directory = 'imported',
  apiUrl = 'http://localhost:12132/api'
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const abortControllerRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    
    if (files.length > 0) {
      handleFiles(files);
    }
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = (newFiles) => {
    const imageFiles = newFiles.filter(file => file.type.startsWith('image/'));
    
    const filesWithPreview = imageFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      preview: URL.createObjectURL(file),
      status: 'pending'
    }));

    setSelectedFiles(prev => [...prev, ...filesWithPreview]);
  };

  const removeFile = (fileId) => {
    setSelectedFiles(prev => {
      const updated = prev.filter(f => f.id !== fileId);
      const removed = prev.find(f => f.id === fileId);
      if (removed?.preview) {
        URL.revokeObjectURL(removed.preview);
      }
      return updated;
    });
  };

  const clearAll = () => {
    selectedFiles.forEach(file => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
    });
    setSelectedFiles([]);
    setUploadResults(null);
    setUploadProgress(0);
    setCurrentFileIndex(0);
  };

  const cancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setUploading(false);
    setUploadProgress(0);
    setCurrentFileIndex(0);
  };

  const uploadImages = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadResults(null);
    setUploadProgress(0);
    setCurrentFileIndex(0);

    abortControllerRef.current = new AbortController();

    const results = {
      total: selectedFiles.length,
      uploaded: 0,
      duplicates: 0,
      upgraded: 0,
      linked: 0,
      rejected: 0,
      errors: 0,
      results: []
    };

    try {
      // Upload files one at a time to match backend /images endpoint
      for (let i = 0; i < selectedFiles.length; i++) {
        if (abortControllerRef.current.signal.aborted) {
          throw new Error('Upload cancelled');
        }

        const fileObj = selectedFiles[i];
        setCurrentFileIndex(i);
        
        const formData = new FormData();
        formData.append('image', fileObj.file);

        try {
          const response = await fetch(`${config.BACKEND_API_ADDRESS}/ingest/images`, {
            method: 'POST',
            body: formData,
            signal: abortControllerRef.current.signal
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `Upload failed: ${response.statusText}`);
          }

          const result = await response.json();
          
          // Map backend status to our status
          let status = 'uploaded';
          if (result.status === 'duplicate') {
            status = 'duplicate';
            results.duplicates++;
          } else if (result.status === 'upgraded') {
            status = 'upgraded';
            results.upgraded++;
          } else if (result.status === 'linked') {
            status = 'linked';
            results.linked++;
          } else if (result.status === 'rejected') {
            status = 'rejected';
            results.rejected++;
          } else if (result.status === 'created') {
            status = 'uploaded';
            results.uploaded++;
          }

          // Update file status
          setSelectedFiles(prev => {
            const newFiles = [...prev];
            const index = newFiles.findIndex(f => f.id === fileObj.id);
            if (index !== -1) {
              newFiles[index] = {
                ...newFiles[index],
                status: status,
                message: result.message,
                imageId: result.image_id,
                fileId: result.file_id,
                dimensions: result.dimensions
              };
            }
            return newFiles;
          });

          results.results.push({
            name: fileObj.name,
            status: status,
            message: result.message,
            imageId: result.image_id,
            fileId: result.file_id
          });

        } catch (error) {
          if (error.name === 'AbortError') {
            console.log('Upload cancelled by user');
            break;
          }
          
          results.errors++;
          results.results.push({
            name: fileObj.name,
            status: 'error',
            message: error.message
          });

          setSelectedFiles(prev => {
            const newFiles = [...prev];
            const index = newFiles.findIndex(f => f.id === fileObj.id);
            if (index !== -1) {
              newFiles[index] = {
                ...newFiles[index],
                status: 'error',
                message: error.message
              };
            }
            return newFiles;
          });
        }

        const progress = Math.round(((i + 1) / selectedFiles.length) * 100);
        setUploadProgress(progress);
      }

      setUploadResults(results);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadResults({
        error: true,
        message: error.message
      });
    } finally {
      setUploading(false);
      abortControllerRef.current = null;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploaded':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'duplicate':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'upgraded':
        return <CheckCircle className="w-5 h-5 text-blue-500" />;
      case 'linked':
        return <CheckCircle className="w-5 h-5 text-purple-500" />;
      case 'rejected':
        return <X className="w-5 h-5 text-orange-500" />;
      case 'error':
        return <X className="w-5 h-5 text-red-500" />;
      default:
        return <FileImage className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'uploaded':
        return 'border-green-200 bg-green-50';
      case 'duplicate':
        return 'border-yellow-200 bg-yellow-50';
      case 'upgraded':
        return 'border-blue-200 bg-blue-50';
      case 'linked':
        return 'border-purple-200 bg-purple-50';
      case 'rejected':
        return 'border-orange-200 bg-orange-50';
      case 'error':
        return 'border-red-200 bg-red-50';
      default:
        return 'border-gray-200 bg-white';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'uploaded':
        return 'Uploaded';
      case 'duplicate':
        return 'Already exists';
      case 'upgraded':
        return 'Upgraded quality';
      case 'linked':
        return 'Linked to existing';
      case 'rejected':
        return 'Lower quality rejected';
      case 'error':
        return 'Error';
      default:
        return 'Pending';
    }
  };

  const formatImageKind = (kind) => {
    return kind.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">Batch Image Upload</h2>
          <p className="mt-1 text-sm text-gray-600">
            Upload multiple images at once. Duplicates will be automatically detected.
          </p>
          <div className="mt-3 flex items-center gap-4 text-xs">
            <div className="flex items-center">
              <FolderOpen className="w-4 h-4 mr-1 text-gray-500" />
              <span className="text-gray-600">Directory:</span>
              <span className="ml-1 bg-blue-100 text-blue-800 px-2 py-1 rounded font-medium">
                {directory}/
              </span>
            </div>
            <div className="flex items-center">
              <Tag className="w-4 h-4 mr-1 text-gray-500" />
              <span className="text-gray-600">Image Kind:</span>
              <span className="ml-1 bg-green-100 text-green-800 px-2 py-1 rounded font-medium">
                {formatImageKind(imageKind)}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div
            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive 
                ? 'border-blue-400 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              Drag and drop images here, or{' '}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-blue-600 hover:text-blue-500 font-medium"
              >
                browse
              </button>
            </p>
            <p className="mt-1 text-xs text-gray-500">
              PNG, JPG, GIF, WEBP, BMP up to 10MB each
            </p>
          </div>

          {uploading && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                  <span className="text-sm font-medium text-gray-700">
                    Uploading {currentFileIndex + 1} of {selectedFiles.length} images...
                  </span>
                </div>
                <button
                  onClick={cancelUpload}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  Cancel
                </button>
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-blue-600 h-full rounded-full transition-all duration-300 ease-out relative"
                  style={{ width: `${uploadProgress}%` }}
                >
                  <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                </div>
              </div>
              
              <div className="mt-2 flex justify-between text-xs text-gray-600">
                <span>{uploadProgress}% Complete</span>
                <span>
                  {selectedFiles.filter(f => f.status !== 'pending').length} processed
                </span>
              </div>
            </div>
          )}

          {uploadResults && !uploadResults.error && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-blue-900">Upload Complete</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Total: {uploadResults.total} | 
                    New: {uploadResults.uploaded} | 
                    Duplicates: {uploadResults.duplicates} | 
                    Upgraded: {uploadResults.upgraded} | 
                    Linked: {uploadResults.linked} | 
                    Rejected: {uploadResults.rejected} | 
                    Errors: {uploadResults.errors}
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    Saved to: <span className="font-medium">{directory}</span> as <span className="font-medium">{formatImageKind(imageKind)}</span>
                  </p>
                </div>
                <button
                  onClick={clearAll}
                  className="px-4 py-2 text-sm bg-white text-blue-600 rounded-md hover:bg-blue-50 border border-blue-200"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}

          {uploadResults?.error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                <p className="text-sm text-red-700">{uploadResults.message}</p>
              </div>
            </div>
          )}

          {selectedFiles.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Selected Images</h3>
                  <p className="text-sm text-gray-600 mt-1">{selectedFiles.length} file(s) ready to upload</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={uploadImages}
                    disabled={uploading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Upload All
                      </>
                    )}
                  </button>
                  <button
                    onClick={clearAll}
                    disabled={uploading}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Clear All
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {selectedFiles.map(file => {
                  const nameParts = file.name.split('.');
                  const extension = nameParts.length > 1 ? nameParts.pop() : '';
                  const baseName = nameParts.join('.');
                  
                  return (
                    <div
                      key={file.id}
                      className={`relative rounded-lg border-2 p-2 transition-all ${getStatusColor(file.status)}`}
                    >
                      <div className="relative aspect-square mb-2">
                        <img
                          src={file.preview}
                          alt={file.name}
                          className="w-full h-full object-cover rounded"
                        />
                        <div className="absolute top-2 right-2">
                          {getStatusIcon(file.status)}
                        </div>
                        {!uploading && (
                          <button
                            onClick={() => removeFile(file.id)}
                            className="absolute top-2 left-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100"
                          >
                            <X className="w-4 h-4 text-gray-600" />
                          </button>
                        )}
                      </div>

                      <div className="px-1">
                        <p className="text-xs font-medium text-gray-800 truncate" title={baseName}>
                          {baseName}
                        </p>
                        <p className="text-xs text-gray-500">
                          .{extension} • {formatFileSize(file.size)}
                        </p>
                        {file.status !== 'pending' && (
                          <p className="text-xs mt-1 font-medium" style={{
                            color: file.status === 'uploaded' ? '#059669' :
                                   file.status === 'duplicate' ? '#d97706' :
                                   file.status === 'upgraded' ? '#2563eb' :
                                   file.status === 'linked' ? '#7c3aed' :
                                   file.status === 'rejected' ? '#ea580c' :
                                   '#dc2626'
                          }}>
                            {getStatusLabel(file.status)}
                          </p>
                        )}
                        {file.message && file.status === 'error' && (
                          <p className="text-xs text-red-600 mt-1" title={file.message}>
                            {file.message.length > 40 ? file.message.substring(0, 40) + '...' : file.message}
                          </p>
                        )}
                        {file.dimensions && (
                          <p className="text-xs text-gray-400 mt-1">
                            {file.dimensions}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BatchImageUploader;